import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Prompt } from "react-router-dom";
import { Intent, Tab, Tabs } from "@blueprintjs/core";
import { DpmsTypes, DpmsTypes_Descriptions } from "csd.phoenix.models/DpmsTypes";
import { EntityType, UNKNOWN_ENTITY_TYPE } from "../../../../models/enums";
import { ISyncSchedule } from "../../../../models/SyncSchedule";
import { ISyncEntityType } from "../../../../models/SyncEntityType";
import { IEntityTypeDependency } from "../../../../models/EntityTypeDependency";
import { ISupportedEntityType } from "../../../../models/CustomerSyncEntityTypes";
import { 
  getSupportedEntityTypes, 
  getEntityTypeDependencies, 
  parseEntityType
} from "../../../../stores/syncSchedulesStore";
import { updateCustomer } from "../../../../stores/customersStore";
import { showToast } from "../../../../services/ToasterService";
import { parseApiError } from "../../../../utils/apiErrorUtils";
import useAuthService from "../../../../services/authService";
import SuccessDialog from "./SuccessDialog";
import ErrorDialog from "./ErrorDialog";
import ConfirmationDialog from "./ConfirmationDialog";
import ScheduleTabPanel from "./ScheduleTabPanel";
import ActionButtons from "./ActionButtons";
import LoadingState from "./LoadingState";
import SyncSchedulePanelHeader from "./SyncSchedulePanelHeader";

interface ISyncSchedulePanelProps {
  customerId: string;
  dpmsType: DpmsTypes;
  syncSchedules: ISyncSchedule[];
  tenantInstallationId?: string;
  onNotesUpdate?: (notes: string[]) => void;
  onHasChangesChange?: (hasChanges: boolean) => void;
}

const getScheduleLabel = (schedule: ISyncSchedule, index: number): string => {
  // Get the schedule type (default to "Recurring" if not specified)
  const scheduleType = schedule.syncScheduleType || "Recurring";
  
  // Parse run interval for friendly display
  const interval = schedule.runInterval || "";
  let intervalDisplay = "";
  
  if (interval && interval !== "00:00:00") {
    const parts = interval.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    
    if (hours > 0) {
      intervalDisplay = `${hours}hr`;
    } else if (minutes > 0) {
      intervalDisplay = `${minutes}min`;
    }
  }
  
  // Build the label: Type (interval)
  let label = scheduleType;
  if (intervalDisplay) {
    label += ` (${intervalDisplay})`;
  }
  
  // Fallback if no interval or type
  if (!intervalDisplay && !schedule.syncScheduleType) {
    label = `Schedule ${index + 1} (Priority ${schedule.priority})`;
  }
  
  return label;
};

const getDpmsTypeName = (dpmsType: DpmsTypes): string => {
  return DpmsTypes_Descriptions[dpmsType] ?? `Unknown DPMS (${dpmsType})`;
};

const SyncSchedulePanel: React.FC<ISyncSchedulePanelProps> = ({
  customerId,
  dpmsType,
  syncSchedules,
  tenantInstallationId,
  onNotesUpdate,
  onHasChangesChange
}) => {
  const { getLoggedInUser } = useAuthService();
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [errorDetails, setErrorDetails] = useState<string>("");
  
  const [supportedEntityTypes, setSupportedEntityTypes] = useState<ISupportedEntityType[]>([]);
  const [dependencies, setDependencies] = useState<IEntityTypeDependency[]>([]);
  
  // Derive requiresEgress from tenantInstallationId (egress customers have tenant installations)
  const requiresEgress = !!tenantInstallationId;
  //const requiresEgress = false; // Uncomment to test non-egress behavior
  
  // Build display name map from supported entity types
  const displayNameMap = useMemo(() => {
    const map = new Map<EntityType, string>();
    supportedEntityTypes.forEach(item => map.set(item.type, item.displayName));
    return map;
  }, [supportedEntityTypes]);
  
  // Helper to get display name for an entity type
  const getDisplayName = useCallback((entityType: EntityType): string => {
    return displayNameMap.get(entityType) ?? entityType ?? "Unknown";
  }, [displayNameMap]);
  
  const [activeScheduleIndex, setActiveScheduleIndex] = useState(0);
  const [pendingChangesBySchedule, setPendingChangesBySchedule] = useState<{
    [scheduleIndex: number]: EntityType[]
  }>({});
  const [originalEntitiesBySchedule, setOriginalEntitiesBySchedule] = useState<{
    [scheduleIndex: number]: EntityType[]
  }>({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset panel state when customer or dpmsType changes
  useEffect(() => {
    setLoaded(false);
    setSupportedEntityTypes([]);
    setDependencies([]);
    setActiveScheduleIndex(0);
    setPendingChangesBySchedule({});
    setOriginalEntitiesBySchedule({});
    setError(null);
  }, [customerId, dpmsType]);

  // Reload data when syncSchedules prop changes (e.g., after save and parent refresh)
  useEffect(() => {
    if (loaded && syncSchedules.length > 0) {
      // Re-initialize tracking state with updated syncSchedules
      const updatedChangesBySchedule: { [key: number]: EntityType[] } = {};
      const updatedOriginalBySchedule: { [key: number]: EntityType[] } = {};
      
      syncSchedules.forEach((schedule, index) => {
        const scheduleEntities = schedule.entityTypesToSync.map(et => parseEntityType(et.entityType));
        updatedChangesBySchedule[index] = [...scheduleEntities];
        updatedOriginalBySchedule[index] = [...scheduleEntities];
      });
      
      setPendingChangesBySchedule(updatedChangesBySchedule);
      setOriginalEntitiesBySchedule(updatedOriginalBySchedule);
    }
  }, [syncSchedules, loaded]);

  // Load data on demand
  const handleLoadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [supportedTypes, depData] = await Promise.all([
        getSupportedEntityTypes(dpmsType),
        getEntityTypeDependencies(dpmsType)
      ]);

      // Only update state if component is still mounted
      if (!isMountedRef.current) return;

      // If API returns no supported types, derive from dependencies
      const finalSupportedTypes = supportedTypes.length > 0 
        ? supportedTypes 
        : depData.map(d => ({ type: d.entityType, displayName: d.entityType }));

      setSupportedEntityTypes(finalSupportedTypes);
      setDependencies(depData);
      
      // Initialize per-schedule tracking
      const initialChangesBySchedule: { [key: number]: EntityType[] } = {};
      const initialOriginalBySchedule: { [key: number]: EntityType[] } = {};
      
      syncSchedules.forEach((schedule, index) => {
        // Parse entity types from API to normalized string identifiers
        const scheduleEntities = schedule.entityTypesToSync.map(et => parseEntityType(et.entityType));
        initialChangesBySchedule[index] = [...scheduleEntities];
        initialOriginalBySchedule[index] = [...scheduleEntities];
      });
      
      setPendingChangesBySchedule(initialChangesBySchedule);
      setOriginalEntitiesBySchedule(initialOriginalBySchedule);
      setLoaded(true);
    } catch (err) {
      if (!isMountedRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to load sync schedule data");
      showToast("Failed to load sync schedule data", Intent.DANGER);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [dpmsType, syncSchedules]);

  const handleEntityToggle = useCallback((entityType: EntityType, enabling: boolean) => {
    setPendingChangesBySchedule(prev => {
      const currentScheduleChanges = prev[activeScheduleIndex] || [];
      const updated = enabling
        ? [...currentScheduleChanges, entityType]
        : currentScheduleChanges.filter(e => e !== entityType);
      
      return {
        ...prev,
        [activeScheduleIndex]: updated
      };
    });
  }, [activeScheduleIndex]);

  // Calculate impact summary for a specific schedule - analyzes the final state (pending changes)
  const getScheduleImpactSummary = useCallback((scheduleIndex: number) => {
    const pendingChanges = pendingChangesBySchedule[scheduleIndex] || [];
    const originalEntities = originalEntitiesBySchedule[scheduleIndex] || [];
    
    const addedEntities = pendingChanges.filter(et => !originalEntities.includes(et));
    const removedEntities = originalEntities.filter(et => !pendingChanges.includes(et));
    
    // Create a set of supported entity types for filtering
    const supportedTypes = new Set(supportedEntityTypes.map(st => st.type));
    
    // Group by missing dependency (inverted structure for better UX)
    const missingDepsMap = new Map<EntityType, EntityType[]>();
    
    // Check ALL entities in the final state (pending changes) for missing dependencies
    pendingChanges.forEach(entityType => {
      const dep = dependencies.find(d => d.entityType === entityType);
      if (dep) {
        // Filter out: Unknown (bad data), unsupported types, and satisfied dependencies
        const missingDeps = dep.dependsOn.filter((d: EntityType) => 
          d !== UNKNOWN_ENTITY_TYPE && 
          supportedTypes.has(d) &&  // Only show dependencies that can actually be enabled
          !pendingChanges.includes(d)
        );
        missingDeps.forEach(missingDep => {
          if (!missingDepsMap.has(missingDep)) {
            missingDepsMap.set(missingDep, []);
          }
          missingDepsMap.get(missingDep)!.push(entityType);
        });
      }
    });
    
    // Convert map to array format
    const missingDependencies: Array<{ missingDep: EntityType; requiredBy: EntityType[] }> = [];
    missingDepsMap.forEach((requiredBy, missingDep) => {
      missingDependencies.push({ missingDep, requiredBy });
    });
    
    return {
      addedEntities,
      removedEntities,
      missingDependencies,
      hasIssues: missingDependencies.length > 0
    };
  }, [pendingChangesBySchedule, originalEntitiesBySchedule, dependencies, supportedEntityTypes]);

  const handleInitiateSave = useCallback(() => {
    setShowConfirmDialog(true);
  }, []);

  const generateAutoNote = useCallback((): string => {
    const user = getLoggedInUser();
    const userName = user?.name || 'Unknown User';
    const now = new Date();
    const dateStr = now.toLocaleDateString();
    const timeStr = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    
    const changeLines: string[] = [];
    syncSchedules.forEach((schedule, index) => {
      const pending = pendingChangesBySchedule[index] || [];
      const original = originalEntitiesBySchedule[index] || [];
      
      const added = pending.filter(e => !original.includes(e));
      const removed = original.filter(e => !pending.includes(e));
      
      if (added.length > 0 || removed.length > 0) {
        const scheduleLabel = getScheduleLabel(schedule, index);
        const parts: string[] = [];
        
        if (added.length > 0) {
          parts.push(`+${added.length} ${added.length === 1 ? 'entity' : 'entities'} (${added.map(e => getDisplayName(e)).join(', ')})`);
        }
        if (removed.length > 0) {
          parts.push(`-${removed.length} ${removed.length === 1 ? 'entity' : 'entities'} (${removed.map(e => getDisplayName(e)).join(', ')})`);
        }
        
        changeLines.push(`${scheduleLabel}: ${parts.join(', ')}`);
      }
    });
    
    return `Sync schedule updated by ${userName} on ${dateStr} ${timeStr} via Enrollment Portal. ${changeLines.join('; ')}`;
  }, [getLoggedInUser, syncSchedules, pendingChangesBySchedule, originalEntitiesBySchedule, getDisplayName]);

  const buildCombinedNote = useCallback((autoNote: string, manualNote: string): string => {
    let combinedNote = autoNote || "";
    
    if (manualNote && manualNote.trim()) {
      const separator = " Note: ";
      combinedNote += separator + manualNote.trim();
    }
    
    return combinedNote;
  }, []);

  const handleConfirmSave = useCallback(async (additionalNote: string) => {
    try {
      setSaving(true);

      // Update each schedule with its specific entity selections
      const updatedSchedules = syncSchedules.map((schedule, index) => ({
        ...schedule,
        entityTypesToSync: (pendingChangesBySchedule[index] || []).map(entityType => ({
          entityType: entityType, // Already a string identifier from backend
          overrideDaysBefore: undefined,
          overrideDaysAfter: undefined
        }) as ISyncEntityType)
      }));

      const autoNote = generateAutoNote();
      const combinedNote = buildCombinedNote(autoNote, additionalNote);
      
      // Backend appends notes, so only send the new note(s), not the entire array
      const notesToAppend = combinedNote ? [combinedNote] : [];

      const updatedCustomer = await updateCustomer(customerId, { syncSchedules: updatedSchedules, notes: notesToAppend });
      
      // Only update state if component is still mounted
      if (!isMountedRef.current) return;
      
      // Update original entities to reflect saved state
      // This updates the UI to show the new configuration without a full page refresh
      setOriginalEntitiesBySchedule({ ...pendingChangesBySchedule });
      
      // Update notes without full page refresh
      if (onNotesUpdate && updatedCustomer.notes) {
        onNotesUpdate(updatedCustomer.notes);
      }
      
      // Close confirmation dialog and show success
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);
    } catch (err) {
      if (!isMountedRef.current) return;
      
      // Parse error into user-friendly message
      const { friendlyMessage, technicalDetails } = parseApiError(err, "Unable to save sync schedule changes.");
      
      // Close confirmation dialog and show error dialog
      setShowConfirmDialog(false);
      setErrorMessage(friendlyMessage);
      setErrorDetails(technicalDetails);
      setShowErrorDialog(true);
      
      showToast(friendlyMessage, Intent.DANGER);
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  }, [customerId, syncSchedules, pendingChangesBySchedule, generateAutoNote, buildCombinedNote, onNotesUpdate]);

  const handleCancelSave = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  const handleErrorDialogClose = useCallback(() => {
    setShowErrorDialog(false);
  }, []);

  const handleReset = useCallback(() => {
    setPendingChangesBySchedule(prev => ({
      ...prev,
      [activeScheduleIndex]: [...(originalEntitiesBySchedule[activeScheduleIndex] || [])]
    }));
  }, [activeScheduleIndex, originalEntitiesBySchedule]);

  const handleResetAll = useCallback(() => {
    const resetAll: { [key: number]: EntityType[] } = {};
    Object.keys(originalEntitiesBySchedule).forEach(key => {
      const index = Number(key);
      resetAll[index] = [...(originalEntitiesBySchedule[index] || [])];
    });
    setPendingChangesBySchedule(resetAll);
  }, [originalEntitiesBySchedule]);

  // Check if ANY schedule has changes
  const hasAnyChanges = Object.keys(pendingChangesBySchedule).some(index => {
    const pending = pendingChangesBySchedule[Number(index)] || [];
    const original = originalEntitiesBySchedule[Number(index)] || [];
    return JSON.stringify(pending.sort()) !== JSON.stringify(original.sort());
  });

  // Notify parent of changes state
  useEffect(() => {
    if (onHasChangesChange) {
      onHasChangesChange(hasAnyChanges);
    }
  }, [hasAnyChanges, onHasChangesChange]);

  // Warn on browser close/refresh when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasAnyChanges) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasAnyChanges]);

  // Check if active schedule has changes
  const hasChanges = (() => {
    const pending = pendingChangesBySchedule[activeScheduleIndex] || [];
    const original = originalEntitiesBySchedule[activeScheduleIndex] || [];
    return JSON.stringify(pending.sort()) !== JSON.stringify(original.sort());
  })();

  // Show loading state if not yet loaded, currently loading, or if there's an error
  if (!loaded || loading || error) {
    return <LoadingState loaded={loaded} loading={loading} error={error} onLoadData={handleLoadData} />;
  }

  return (
    <div>
      <Prompt
        when={hasAnyChanges}
        message="You have unsaved sync schedule changes which will be lost. Discard?"
      />
      <SyncSchedulePanelHeader 
        requiresEgress={requiresEgress}
        dpmsTypeName={getDpmsTypeName(dpmsType)}
      />

      <Tabs
        id="sync-schedules"
        selectedTabId={activeScheduleIndex}
        onChange={(newTabId) => setActiveScheduleIndex(newTabId as number)}
        large={true}
      >
        {syncSchedules.map((schedule, index) => {
          const scheduleChanges = pendingChangesBySchedule[index] || [];
          const scheduleOriginal = originalEntitiesBySchedule[index] || [];
          const changeCount = Math.abs(scheduleChanges.length - scheduleOriginal.length);
          const hasScheduleChanges = JSON.stringify(scheduleChanges.sort()) !== JSON.stringify(scheduleOriginal.sort());
          const scheduleImpactSummary = getScheduleImpactSummary(index);
          const scheduleHasChanges = JSON.stringify(scheduleChanges.sort()) !== JSON.stringify(scheduleOriginal.sort());
          
          return (
            <Tab
              id={index}
              key={index}
              title={`${getScheduleLabel(schedule, index)}${hasScheduleChanges ? ` (${changeCount})` : ''}`}
              panel={
                <ScheduleTabPanel
                  schedule={schedule}
                  index={index}
                  scheduleChanges={scheduleChanges}
                  scheduleOriginal={scheduleOriginal}
                  supportedEntityTypes={supportedEntityTypes}
                  dependencies={dependencies}
                  pendingChanges={scheduleChanges}
                  loaded={loaded}
                  requiresEgress={requiresEgress}
                  hasChanges={scheduleHasChanges}
                  impactSummary={scheduleImpactSummary}
                  getScheduleLabel={getScheduleLabel}
                  getDisplayName={getDisplayName}
                  onEntityToggle={handleEntityToggle}
                />
              }
            />
          );
        })}
      </Tabs>

      {/* Action buttons - outside of tabs */}
      <ActionButtons
        hasAnyChanges={hasAnyChanges}
        hasChanges={hasChanges}
        saving={saving}
        onReset={handleReset}
        onResetAll={handleResetAll}
        onSave={handleInitiateSave}
      />

      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
        saving={saving}
        syncSchedules={syncSchedules}
        pendingChangesBySchedule={pendingChangesBySchedule}
        originalEntitiesBySchedule={originalEntitiesBySchedule}
        getScheduleLabel={getScheduleLabel}
        getDisplayName={getDisplayName}
        getScheduleImpactSummary={getScheduleImpactSummary}
        requiresEgress={requiresEgress}
      />
      
      <SuccessDialog
        isOpen={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
      />

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={handleErrorDialogClose}
        errorMessage={errorMessage}
        errorDetails={errorDetails}
      />
    </div>
  );
};

export default SyncSchedulePanel;
